/* LBRGT: Größenberater (snippet lbrgt-groessenberater).
   Opens a native <dialog> next to the variant picker, recomputes the recommended size on
   every keystroke from the rule table rendered by Liquid, and - when that size exists as a
   variant option - offers to select it in the theme's own variant picker.
   No framework, no build step. */

if (!customElements.get('lbrgt-groessenberater')) {
  const FOCUSABLE = 'button:not([disabled]):not([hidden]), input:not([disabled]), select:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';

  class LbrgtGroessenberater extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('.lbrgt-ga__dialog');
      this.trigger = this.querySelector('[data-ga-open]');
      this.result = this.querySelector('[data-ga-result]');
      this.resultText = this.querySelector('[data-ga-result-text]');
      this.applyBtn = this.querySelector('[data-ga-apply]');
      this.heightInput = this.querySelector('[data-ga-height]');
      this.weightInput = this.querySelector('[data-ga-weight]');
      this.fitSelect = this.querySelector('[data-ga-fit]');

      if (!this.dialog || !this.trigger || !this.resultText) return;

      this.data = LbrgtGroessenberater.parseData(this.querySelector('[data-ga-data]'));
      if (!this.data.rules.length) return;

      // A browser without <dialog> would show the panel inline, so the trigger stays hidden there
      if (typeof this.dialog.showModal !== 'function') {
        this.trigger.hidden = true;
        return;
      }

      this.match = null;

      this.onKeydown = this.handleKeydown.bind(this);
      this.trigger.addEventListener('click', () => this.open());
      this.querySelectorAll('[data-ga-close]').forEach((btn) => btn.addEventListener('click', () => this.dialog.close()));

      // Clicking the backdrop: the click lands on the <dialog> itself, never on the panel
      this.dialog.addEventListener('click', (evt) => {
        if (evt.target === this.dialog) this.dialog.close();
      });

      // Escape fires 'cancel' first and 'close' afterwards, so 'close' covers every exit
      this.dialog.addEventListener('close', () => this.handleClose());

      [this.heightInput, this.weightInput, this.fitSelect].forEach((field) => {
        if (!field) return;
        field.addEventListener('input', () => this.update());
        field.addEventListener('change', () => this.update());
      });

      if (this.applyBtn) this.applyBtn.addEventListener('click', () => this.applySize());

      /* The trigger sits inside the variant picker, so the dialog's markup starts life inside
         it too and inherits that component's typography and flex rules - the heading came out
         one letter per line. A modal dialog renders in the top layer anyway, so its position in
         the DOM is irrelevant to the user; moving it to <body> puts it out of reach of any
         section's stylesheet. The element references above stay valid, children move with it. */
      if (this.dialog.parentElement !== document.body) {
        document.body.appendChild(this.dialog);
        this.dialogDetached = true;
      }

      this.update();
      this.setupSetPlacement();
    }

    /* Kaching sets (2x/3x) hide the theme's variant picker and show their own size selects per
       shirt, and the trigger used to vanish with the picker. While the picker is hidden the
       trigger moves into a row right above the Kaching block, and goes home as soon as a single
       item is selected again (27.10.). Only the button moves: moving this element would run
       disconnectedCallback and take the dialog down with it. */
    setupSetPlacement() {
      const picker = this.closest('variant-picker');
      const scope = this.closest('.product-info') || this.closest('.shopify-section');
      if (!picker || !scope) return;

      this.home = this.trigger.parentElement;
      let queued = false;

      const sync = () => {
        queued = false;
        const bundle = scope.querySelector('kaching-bundle');
        const inSet = !!bundle && picker.getClientRects().length === 0;

        if (inSet) {
          if (!this.setRow) {
            this.setRow = document.createElement('div');
            this.setRow.className = 'lbrgt-ga lbrgt-ga--set';
          }
          const block = bundle.closest('.product-info__block') || bundle;
          if (this.setRow.nextElementSibling !== block) block.before(this.setRow);
          if (this.trigger.parentElement !== this.setRow) {
            this.setRow.appendChild(this.trigger);
            this.update();
          }
        } else if (this.trigger.parentElement !== this.home) {
          this.home.insertBefore(this.trigger, this.home.firstChild);
          if (this.setRow) this.setRow.remove();
          this.update();
        }
      };

      // Our own moves are mutations too; sync only writes on a real change, so this settles
      const queue = () => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(sync);
      };

      new MutationObserver(queue).observe(scope, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['class', 'style', 'hidden'],
      });
      queue();
    }

    /** The size selects of the selected Kaching set, while the trigger sits above the sets */
    getSetSelects() {
      if (!this.setRow || this.trigger.parentElement !== this.setRow) return [];
      const bar = document.querySelector('.kaching-bundles__bar--selected');
      return bar ? Array.from(bar.querySelectorAll('select.kaching-bundles__bar-variant-select')) : [];
    }

    disconnectedCallback() {
      if (this.dialogDetached && this.dialog && this.dialog.parentElement === document.body) {
        this.dialog.remove();
      }
    }

    static parseData(node) {
      const empty = { rules: [], sizes: [], noMatch: '', idle: '', resultPrefix: '', applyLabel: '', soldOutSuffix: '' };
      if (!node) return empty;
      try {
        return Object.assign(empty, JSON.parse(node.textContent));
      } catch (err) {
        return empty;
      }
    }

    open() {
      this.lastFocused = document.activeElement;
      this.dialog.showModal();
      document.addEventListener('keydown', this.onKeydown, true);
      // On touch screens focusing the first field opens the keyboard over the sheet right away
      // (and iOS zooms into it); start on the dialog's first control there instead.
      const touch = window.matchMedia('(pointer: coarse)').matches;
      const first = (!touch && this.heightInput) || this.dialog.querySelector(FOCUSABLE);
      if (first) first.focus({ preventScroll: true });
    }

    handleClose() {
      document.removeEventListener('keydown', this.onKeydown, true);
      // Focus goes back to where it came from, which for every normal path is the trigger
      const target = this.lastFocused && this.lastFocused.isConnected ? this.lastFocused : this.trigger;
      if (target && typeof target.focus === 'function') target.focus();
    }

    /** Keeps Tab inside the panel. showModal() already blocks the page behind it,
     *  but without this the focus ring still walks into the browser chrome. */
    handleKeydown(evt) {
      if (evt.key !== 'Tab' || !this.dialog.open) return;
      const items = Array.from(this.dialog.querySelectorAll(FOCUSABLE)).filter(
        (el) => !el.hidden && el.offsetParent !== null
      );
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;

      if (evt.shiftKey && (active === first || !this.dialog.contains(active))) {
        evt.preventDefault();
        last.focus();
      } else if (!evt.shiftKey && active === last) {
        evt.preventDefault();
        first.focus();
      }
    }

    update() {
      const height = parseInt(this.heightInput && this.heightInput.value, 10);
      const weight = parseInt(this.weightInput && this.weightInput.value, 10);
      const fit = (this.fitSelect && this.fitSelect.value ? this.fitSelect.value : '').toLowerCase();

      this.match = null;

      // Nothing typed yet is not a failure - say what is missing instead of guessing a size
      if (!Number.isFinite(height) || !Number.isFinite(weight)) {
        this.render(this.data.idle, true);
        return;
      }

      const rule = this.data.rules.find(
        // A rule without a fit column applies to any fit
        (r) => height >= r.h[0] && height <= r.h[1] && weight >= r.w[0] && weight <= r.w[1] && (r.f === '' || r.f === fit)
      );

      if (!rule) {
        this.render(this.data.noMatch, true);
        return;
      }

      this.match = this.findSize(rule.s);
      let text = `${this.data.resultPrefix} ${rule.s}`.trim();
      if (this.match && !this.match.a) text = `${text} ${this.data.soldOutSuffix}`.trim();
      this.render(text, false);
    }

    // Only touch text that changes: 'change' fires on blur, i.e. between mousedown and mouseup on
    // "Größe wählen", and rewriting the button's text there swallowed that click (retest 22.10.)
    render(text, isEmpty) {
      if (this.resultText.textContent !== text) this.resultText.textContent = text;
      if (this.result) this.result.classList.toggle('lbrgt-ga__result--empty', !!isEmpty);

      if (!this.applyBtn) return;
      const canApply = !isEmpty && this.match && (this.getSetSelects().length ? true : this.match.a && this.getOptionNode(this.match.id));
      if (this.applyBtn.hidden !== !canApply) this.applyBtn.hidden = !canApply;
      if (canApply && this.applyBtn.textContent !== this.data.applyLabel) this.applyBtn.textContent = this.data.applyLabel;
    }

    /** Matches the recommended size against the product's own option values, case- and
     *  space-insensitively, so "XL" still finds an option written " xl ". */
    findSize(size) {
      const wanted = String(size).trim().toLowerCase();
      return this.data.sizes.find((s) => String(s.v).trim().toLowerCase() === wanted) || null;
    }

    getOptionNode(id) {
      return id ? document.getElementById(id) : null;
    }

    /** Selects the size in the theme's variant picker. Radio buttons (listed style) are
     *  checked and fire 'change'; the dropdown style renders <li role="option"> under the
     *  same id, and there a click is what the custom-select element listens for. */
    applySize() {
      if (!this.match) return;

      /* In a set the size goes to every shirt of the selected set. Only selects that offer
         that size change - the free socks (35-38 ...) never match a shirt size. */
      const setSelects = this.getSetSelects();
      if (setSelects.length) {
        const wanted = String(this.match.v).trim().toLowerCase();
        setSelects.forEach((select) => {
          const option = Array.from(select.options).find((o) => o.value.trim().toLowerCase() === wanted);
          if (!option || select.value === option.value) return;
          select.value = option.value;
          select.dispatchEvent(new Event('input', { bubbles: true }));
          select.dispatchEvent(new Event('change', { bubbles: true }));
        });
        this.dialog.close();
        return;
      }

      const node = this.getOptionNode(this.match.id);
      if (!node) return;

      if (node.tagName === 'INPUT') {
        if (!node.checked) {
          node.checked = true;
          node.dispatchEvent(new Event('change', { bubbles: true }));
        }
      } else {
        node.click();
      }

      this.dialog.close();
    }
  }

  customElements.define('lbrgt-groessenberater', LbrgtGroessenberater);
}
